import NiceModal from '@ebay/nice-modal-react';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import AutoComplete from 'components/field/AutoComplete';
import PhoneNumber from 'components/field/PhoneNumber';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import Accordion from 'components/global/Accordion';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';

import WarehouseFormShort from 'pages/Warehouses/WarehouseFormShort';

import { AUTOCOMPLETE } from 'services/consts';

import * as S from './DeliveryDetails.styled';

const DeliveryDetails = ({ formMode }) => {
	const { t } = useTranslation();
	const isAuthorized = useAccessControl();
	const {
		watch,
		setValue,
		formState: { isLoading: isFormLoading, isSubmitting }
	} = useFormContext();
	const isLoading = isFormLoading || isSubmitting;

	const warehouseAutocompleteColumns = [{ accessor: 'Name', width: 200 }];

	return (
		<>
			<S.AccordionContainer>
				<Accordion header="deliveryAndContactDetails" defaultVisible>
					<FormItem>
						<Typography variant="p2" fontWeight="700">
							deliveryDetails
						</Typography>
					</FormItem>
					<FormItem label="originCountry" required isLoading={isLoading}>
						<StringInput value={t(`country.CN`)} disabled />
					</FormItem>

					<FormItem label="warehouseLocation" required isLoading={isLoading}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							columns={warehouseAutocompleteColumns}
							name="warehouseID"
							value={watch('warehouseID')}
							setValue={(value, isFirstRender) => {
								setValue('warehouseID', value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});

								if (!isFirstRender || formMode === 'add') {
									setValue('warehouseAddress', value?.Address ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});

									setValue('warehouseCountry', value?.Country ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});

									setValue('email', value?.ContactEmail ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});
									setValue('phone', value?.Tel ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});
									setValue('firstName', value?.ContactPerson ?? null, {
										shouldValidate: true,
										shouldDirty: true
									});
								}
							}}
							selectionLabelAccessor={['Name']}
							placeholder="selectLocation"
							firstItem={
								isAuthorized('/autocomplete/warehouse/addANewWarehouse')
									? {
											action: () =>
												NiceModal.show(WarehouseFormShort, {
													setWarehouse: (value) => {
														setValue('warehouseID', value ?? null, {
															shouldValidate: true,
															shouldDirty: true
														});
													}
												}),
											label: <Typography color="var(--blue)">addANewWarehouse</Typography>
										}
									: undefined
							}
							disabled={formMode === 'view'}
						/>
						<Typography variant="caption" color="var(--content-3)" className="mt-3">
							warehouseLocationDescription
						</Typography>
					</FormItem>

					{watch('warehouseID') && (
						<>
							<FormItem label="destinationCountry" required isLoading={isLoading}>
								<StringInput value={t(`country.${watch('warehouseCountry')}`)} disabled />
							</FormItem>
							<FormItem label="fullAddressLine" required isLoading={isLoading}>
								<StringInput value={watch('warehouseAddress')} disabled />
							</FormItem>

							<S.Divider />

							<FormItem>
								<Typography variant="p2" fontWeight="700">
									importContactDetails
								</Typography>
							</FormItem>
							<FormItem label="fullName" required isLoading={isLoading}>
								<StringInput name="firstName" disabled={formMode === 'view'} />
							</FormItem>
							<FormItem colSpan={{ md: 6 }} label="phoneNumber" required isLoading={isLoading}>
								<PhoneNumber
									name="phone"
									value={watch('phone')}
									onChange={(e) => {
										setValue('phone', e, { shouldValidate: true, shouldDirty: true });
									}}
									disabled={formMode === 'view'}
								/>
							</FormItem>
							<FormItem colSpan={{ md: 6 }} label="email" isLoading={isLoading}>
								<StringInput name="email" disabled={formMode === 'view'} />
							</FormItem>
						</>
					)}
				</Accordion>
			</S.AccordionContainer>
		</>
	);
};

export default DeliveryDetails;
